import { Source } from '../models/Source'
import DateService from '../services/DateService'

export default class Offer {
    private data: any

    id: string
    name: string
    description: string

    constructor (data: any) {
        this.data = data
        this.id = data._id
        this.name = data.title
        this.description = data.description
    }

    get validFrom (): Date | null {
        if (this.data.start_date) {
            return new Date(this.data.start_date)
        }

        return null
    }

    get validTo (): Date | null {
        if (this.data.expiry_date) {
            return new Date(this.data.expiry_date)
        }

        return null
    }

    get visibleFrom (): Date | null {
        if (this.data.visible_from) {
            return new Date(this.data.visible_from)
        }

        return null
    }

    get visibleTo (): Date | null {
        if (this.data.visible_to) {
            return new Date(this.data.visible_to)
        }

        return null
    }

    get shouldShowValidFrom (): boolean {
        const now = new Date()

        if (this.validFrom) {
            return this.validFrom.getTime() > now.getTime()
        }

        return false
    }

    isOnSpecifiedDaysOnly (days: string[]) {
        const rules = this.getAvailableDaysAndTimes()
    
        if (rules.length !== days.length) return false
    
        for (let i = 0; i < rules.length; i++) {
            if (days.indexOf(rules[i].day) === -1) return false
        }
    
        return true
    }
    
    isWeekends (): boolean {
        const days = ['Saturday', 'Sunday']
        return this.isOnSpecifiedDaysOnly(days)
    }
    
    isWeekdays (): boolean {
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
        return this.isOnSpecifiedDaysOnly(days)
    }
    
    isEveryDay (): boolean {
        return this.getAvailableDaysAndTimes().length === 7
    }
    
    getAvailableDaysAndTimes (): any[] {
        const days = DateService.getDayFullNames()
        let avail: any[] = []
        let day
        let rules

        days.forEach((day: string) => {
            rules = this.data.rules[day.toLowerCase()]

            if (!rules.available) return

            avail.push({
                day: day,
                from: rules.from,
                to: rules.to
            })
        })

        return avail
    }

    get minimumPeople (): number | null {
        return this.data.rules.min_people
    }

    get maximumPeople (): number | null {
        return this.data.rules.max_people
    }

    /**
     * Determines if the visibility date range has been specified for this offer
     * @return {Boolean}
     */
    isVisibilitySet (): boolean {
      return this.visibleFrom !== null || this.visibleTo !== null
    }

    /**
     * Determines whether the offer has expired or not
     * @return {Boolean}
     */
    hasExpired (): boolean {
        if (!this.validTo) return false

        const now = new Date()

        return now.getTime() >= this.validTo.getTime()
    }

    /**
     * Determines whether or not the offer has started
     * @return {Boolean}
    */
    hasStarted (): boolean {
        if (this.validFrom) {
            const now = new Date()
            const start = this.validFrom
            return now >= start
        }

        return true
    }

    shouldDisplay (source: Source): boolean {
        return this.isVisible() && !this.hasExpired() && this.isSourceEligible(source)
    }

    /**
     * Looks at the visible_from and visible_to, and works out whether or not
     * the offer should be visible today.
     * @return {Boolean}
     */
    isVisible (): boolean {
        // Visible_to/Visible_from is an optional field.
        if (!this.isVisibilitySet()) {
            // Don't show it if it hasn't started yet.
            return this.hasStarted()
        }

        const from = this.visibleFrom
        const to = this.visibleTo
        const today = new Date().getTime()

        if (from && from.getTime() > today) return false
        if (to && to.getTime() < today) return false
    
        return true
    }

    /**
     * Determines whether this offer only applies to bookings made from the
     * partner's website
     * @return {Boolean}
     */
    isPartnerOnly () {
        return this.data.availability && this.data.availability === Source.partner
    }

    /**
     * Determines whether or not this offer should be displayed based on the source
     * @param {String} source The source of the widget
     * @return {Boolean}
     */
    isSourceEligible (source: string) {
        if (!this.data.availability || this.data.availability === '') {
            return true
        }

        if (this.data.availability === source) {
            return true
        }

        return false
    }
}



// WEBPACK FOOTER //
// ./src/models/Offer.ts